import { CompleteKundali, Language, PlanetName, ZodiacSign } from '../types/kundali';
import { LifeThemeId } from '../types/advancedLifeAnalysis';
import { FiveRuleHouseAssessment, FiveRuleFactor, FiveRuleAssessmentNature } from '../types/v517';
import { ZODIAC_LORDS } from './vedicEngine';

/* ==========================================================================
   VEDANG JYOTISHAM V5.17 — FIVE-RULE HOUSE ANALYSIS ENGINE
   Deterministic 5-Rule Framework for Life Themes & House Assessments
   ========================================================================== */

export const THEME_PRIMARY_HOUSES: Record<LifeThemeId, number> = {
  career_job: 10,
  wealth_finance: 2,
  marriage_relationship: 7,
  house_property: 4,
  vehicle: 4,
  children: 5,
  education: 4,
  health_vitality: 1,
  business: 7,
  foreign_travel: 12,
  litigation_competition: 6,
  spirituality: 9,
  family_parents: 4
};

export const THEME_PRIMARY_KARAKAS: Record<LifeThemeId, PlanetName> = {
  career_job: 'Sun',
  wealth_finance: 'Jupiter',
  marriage_relationship: 'Venus',
  house_property: 'Mars',
  vehicle: 'Venus',
  children: 'Jupiter',
  education: 'Mercury',
  health_vitality: 'Sun',
  business: 'Mercury',
  foreign_travel: 'Rahu',
  litigation_competition: 'Mars',
  spirituality: 'Jupiter',
  family_parents: 'Moon'
};

const NATURAL_BENEFICS: PlanetName[] = ['Jupiter', 'Venus', 'Mercury', 'Moon'];
const NATURAL_MALEFICS: PlanetName[] = ['Saturn', 'Mars', 'Rahu', 'Ketu', 'Sun'];

export function analyzeFiveRuleHouse(
  kundali: CompleteKundali,
  themeId: LifeThemeId,
  lang: Language = 'en'
): FiveRuleHouseAssessment {
  const isHi = lang === 'hi';
  const targetHouseNum = THEME_PRIMARY_HOUSES[themeId] || 1;
  const primaryKarakaName = THEME_PRIMARY_KARAKAS[themeId] || 'Jupiter';

  const houseObj = kundali.houses.find(h => h.houseNumber === targetHouseNum) || kundali.houses[0];
  const houseSign = houseObj.sign;
  const houseLordName = (ZODIAC_LORDS[houseSign] || 'Mars') as PlanetName;

  const occupants = kundali.planets.filter(p => p.house === targetHouseNum);
  const lordPlanet = kundali.planets.find(p => p.name === houseLordName);
  const karakaPlanet = kundali.planets.find(p => p.name === primaryKarakaName);

  const supportingInfluences: string[] = [];
  const contradictingInfluences: string[] = [];

  // 1. Rule 1: Bhava (House) evaluation
  let bhavaScore = 0;
  let bhavaFinding = '';
  let bhavaFindingHi = '';
  let bhavaPromise: FiveRuleFactor['promiseTerminology'] = 'Moderate';

  const beneficOccupants = occupants.filter(p => NATURAL_BENEFICS.includes(p.name));
  const maleficOccupants = occupants.filter(p => NATURAL_MALEFICS.includes(p.name));

  if (beneficOccupants.length > 0 && maleficOccupants.length === 0) {
    bhavaScore += 6;
    bhavaPromise = 'Strong';
    bhavaFinding = `House ${targetHouseNum} (${houseSign}) contains benefic planets (${beneficOccupants.map(p => p.name).join(', ')}), generating auspicious foundation.`;
    bhavaFindingHi = `भाव ${targetHouseNum} (${houseSign}) में शुभ ग्रह (${beneficOccupants.map(p => p.name).join(', ')}) विराजमान हैं, जो उत्कृष्ट आधार निर्मित कर रहे हैं।`;
    supportingInfluences.push(`Benefic occupancy of ${beneficOccupants.map(p => p.name).join(', ')} in House ${targetHouseNum}`);
  } else if (maleficOccupants.length > 0 && beneficOccupants.length === 0) {
    bhavaScore -= 4;
    bhavaPromise = 'Challenged';
    bhavaFinding = `House ${targetHouseNum} (${houseSign}) hosts malefic energy (${maleficOccupants.map(p => p.name).join(', ')}), demanding perseverance and remedy.`;
    bhavaFindingHi = `भाव ${targetHouseNum} (${houseSign}) में पापी/क्रूर ग्रह (${maleficOccupants.map(p => p.name).join(', ')}) उपस्थित हैं, जो धैर्य व शांति की मांग करते हैं।`;
    contradictingInfluences.push(`Malefic presence of ${maleficOccupants.map(p => p.name).join(', ')} in House ${targetHouseNum}`);
  } else if (beneficOccupants.length > 0 && maleficOccupants.length > 0) {
    bhavaScore += 2;
    bhavaPromise = 'Mixed';
    bhavaFinding = `House ${targetHouseNum} contains mixed planetary energies (${occupants.map(p => p.name).join(', ')}), conferring balanced outcomes.`;
    bhavaFindingHi = `भाव ${targetHouseNum} में मिश्रित ग्रहों का प्रभाव है, जिससे मिश्रित परिणाम प्राप्त होंगे।`;
  } else {
    bhavaScore += 2;
    bhavaPromise = 'Favorable';
    bhavaFinding = `House ${targetHouseNum} (${houseSign}) is unoccupied and receptive, governed directly by lord ${houseLordName}.`;
    bhavaFindingHi = `भाव ${targetHouseNum} (${houseSign}) रिक्त एवं शांत है, जिसका प्रत्यक्ष संचालन स्वामी ${houseLordName} द्वारा हो रहा है।`;
  }

  const factor1: FiveRuleFactor = {
    ruleNumber: 1,
    factorName: 'Bhava (House)',
    findingText: bhavaFinding,
    findingTextHi: bhavaFindingHi,
    astrologyTerminology: `House ${targetHouseNum} in ${houseSign} (${occupants.length} occupants)`,
    promiseTerminology: bhavaPromise,
    effectText: bhavaScore >= 4 ? 'Auspicious foundational strength' : bhavaScore < 0 ? 'Requires attention & effort' : 'Stable foundational energy',
    effectTextHi: bhavaScore >= 4 ? 'शुभ व फलदायी आधार' : bhavaScore < 0 ? 'सतर्कता व साधना की आवश्यकता' : 'संतुलित प्रभाव',
    scoreContribution: bhavaScore,
    isAfflicted: maleficOccupants.length > 0,
    beneficInfluences: beneficOccupants.map(p => p.name),
    maleficInfluences: maleficOccupants.map(p => p.name)
  };

  // 2. Rule 2: Bhavesh (House Lord) evaluation
  let bhaveshScore = 0;
  let bhaveshPromise: FiveRuleFactor['promiseTerminology'] = 'Moderate';
  let bhaveshFinding = '';
  let bhaveshFindingHi = '';

  if (lordPlanet) {
    const isLordInKendraOrTrikona = [1, 4, 7, 10, 5, 9].includes(lordPlanet.house);
    const isLordInDusthana = [6, 8, 12].includes(lordPlanet.house);
    const isLordExalted = lordPlanet.dignity === 'Exalted';
    const isLordOwn = lordPlanet.dignity === 'Own House';

    if (isLordExalted || isLordOwn) {
      bhaveshScore += 8;
      bhaveshPromise = 'Strong';
      bhaveshFinding = `House Lord ${houseLordName} is exceptionally dignified (${isLordExalted ? 'Exalted' : 'Own Sign'}) in House ${lordPlanet.house}.`;
      bhaveshFindingHi = `भावेश ${houseLordName} भाव ${lordPlanet.house} में स्वग्रही/उच्च स्थिति में पूर्ण बली हैं।`;
      supportingInfluences.push(`Dignified placement of Lord ${houseLordName}`);
    } else if (isLordInKendraOrTrikona) {
      bhaveshScore += 5;
      bhaveshPromise = 'Favorable';
      bhaveshFinding = `House Lord ${houseLordName} is auspiciously placed in Kendra/Trikona (House ${lordPlanet.house} in ${lordPlanet.sign}).`;
      bhaveshFindingHi = `भावेश ${houseLordName} केंद्र/त्रिकोण (भाव ${lordPlanet.house}, ${lordPlanet.sign}) में शुभ स्थित हैं।`;
      supportingInfluences.push(`Kendra/Trikona placement of Lord ${houseLordName}`);
    } else if (isLordInDusthana) {
      bhaveshScore -= 3;
      bhaveshPromise = 'Challenged';
      bhaveshFinding = `House Lord ${houseLordName} resides in Dusthana (House ${lordPlanet.house}), signaling delayed manifestation or effort.`;
      bhaveshFindingHi = `भावेश ${houseLordName} त्रिक भाव (${lordPlanet.house}) में हैं, जिससे विलंब या अधिक प्रयास अपेक्षित है।`;
      contradictingInfluences.push(`Dusthana placement of Lord ${houseLordName}`);
    } else {
      bhaveshScore += 3;
      bhaveshPromise = 'Moderate';
      bhaveshFinding = `House Lord ${houseLordName} is placed in House ${lordPlanet.house} (${lordPlanet.sign}), maintaining steady continuity.`;
      bhaveshFindingHi = `भावेश ${houseLordName} भाव ${lordPlanet.house} (${lordPlanet.sign}) में सामान्य शुभ स्थिति में हैं।`;
    }
  }

  const factor2: FiveRuleFactor = {
    ruleNumber: 2,
    factorName: 'Bhavesh (House Lord)',
    findingText: bhaveshFinding,
    findingTextHi: bhaveshFindingHi,
    astrologyTerminology: `${houseLordName} as Lord of ${targetHouseNum} (Placed in H${lordPlanet?.house || '?'})`,
    promiseTerminology: bhaveshPromise,
    effectText: bhaveshScore >= 5 ? 'Promotes rapid growth and achievement' : bhaveshScore < 0 ? 'Requires patience and remedial support' : 'Steady progressive results',
    effectTextHi: bhaveshScore >= 5 ? 'शीघ्र प्रगति व सफलता प्रदायक' : bhaveshScore < 0 ? 'धैर्य व शांति उपायों की आवश्यकता' : 'सतत प्रगति',
    scoreContribution: bhaveshScore,
    isAfflicted: bhaveshScore < 0,
    beneficInfluences: bhaveshScore > 0 ? [houseLordName] : [],
    maleficInfluences: bhaveshScore < 0 ? [houseLordName] : []
  };

  // 3. Rule 3: Karaka (Significator)
  let karakaScore = 0;
  let karakaPromise: FiveRuleFactor['promiseTerminology'] = 'Moderate';
  let karakaFinding = '';
  let karakaFindingHi = '';

  if (karakaPlanet) {
    const isKarakaExalted = karakaPlanet.dignity === 'Exalted';
    const isKarakaOwn = karakaPlanet.dignity === 'Own House';
    const isKarakaDebilitated = karakaPlanet.dignity === 'Debilitated';

    if (isKarakaExalted || isKarakaOwn || [1, 4, 5, 9, 10, 11].includes(karakaPlanet.house)) {
      karakaScore += 6;
      karakaPromise = 'Strong';
      karakaFinding = `Primary Karaka ${primaryKarakaName} is vibrant and well-placed in House ${karakaPlanet.house} (${karakaPlanet.sign}).`;
      karakaFindingHi = `प्रमुख कारक ${primaryKarakaName} भाव ${karakaPlanet.house} (${karakaPlanet.sign}) में शुभ व समर्थ स्थिति में हैं।`;
      supportingInfluences.push(`Vibrant Karaka ${primaryKarakaName} in House ${karakaPlanet.house}`);
    } else if (isKarakaDebilitated || [6, 8, 12].includes(karakaPlanet.house)) {
      karakaScore -= 2;
      karakaPromise = 'Challenged';
      karakaFinding = `Primary Karaka ${primaryKarakaName} is in ${isKarakaDebilitated ? 'Debilitation' : 'House ' + karakaPlanet.house}, advising spiritual alignment.`;
      karakaFindingHi = `प्रमुख कारक ${primaryKarakaName} ${isKarakaDebilitated ? 'नीच' : 'भाव ' + karakaPlanet.house} में हैं, जिससे कारक बल संवर्धन आवश्यक है।`;
      contradictingInfluences.push(`Weakness/Dusthana placement of Karaka ${primaryKarakaName}`);
    } else {
      karakaScore += 3;
      karakaPromise = 'Favorable';
      karakaFinding = `Primary Karaka ${primaryKarakaName} is placed stably in House ${karakaPlanet.house}.`;
      karakaFindingHi = `प्रमुख कारक ${primaryKarakaName} भाव ${karakaPlanet.house} में संतुलित स्थित हैं।`;
    }
  }

  const factor3: FiveRuleFactor = {
    ruleNumber: 3,
    factorName: 'Karaka (Significator)',
    findingText: karakaFinding,
    findingTextHi: karakaFindingHi,
    astrologyTerminology: `${primaryKarakaName} (Natural Significator in H${karakaPlanet?.house || '?'})`,
    promiseTerminology: karakaPromise,
    effectText: karakaScore >= 5 ? 'Empowers natural fruition of goals' : karakaScore < 0 ? 'May cause intermittent hesitation' : 'Steady supportive manifestation',
    effectTextHi: karakaScore >= 5 ? 'उद्देश्यों की सहज सिद्धि में सहायक' : karakaScore < 0 ? 'कार्यों में सामयिक संकोच' : 'अनुकूल सहयोग',
    scoreContribution: karakaScore,
    isAfflicted: karakaScore < 0,
    beneficInfluences: karakaScore > 0 ? [primaryKarakaName] : [],
    maleficInfluences: karakaScore < 0 ? [primaryKarakaName] : []
  };

  // 4. Rule 4: Supporting Factors (Benefic aspects & connections)
  let supportScore = 4;
  const jupiter = kundali.planets.find(p => p.name === 'Jupiter');
  const venus = kundali.planets.find(p => p.name === 'Venus');
  if (jupiter && [1, 5, 9, 4, 10].includes(jupiter.house)) {
    supportScore += 3;
    supportingInfluences.push(`Jupiter benefic anchor from House ${jupiter.house}`);
  }
  if (venus && [1, 4, 7, 10, 5, 9, 2, 11].includes(venus.house)) {
    supportScore += 2;
    supportingInfluences.push(`Venus harmonious radiance from House ${venus.house}`);
  }

  const factor4: FiveRuleFactor = {
    ruleNumber: 4,
    factorName: 'Supporting Factors',
    findingText: supportingInfluences.length > 0 
      ? `Benefic alignments (${supportingInfluences.slice(0, 2).join('; ')}) lend protective strength.`
      : 'Harmonious benefic rays support structural stability.',
    findingTextHi: supportingInfluences.length > 0
      ? `शुभ ग्रहों का प्रभाव जीवन क्षेत्र को सुरक्षा व संवर्धन प्रदान कर रहा है।`
      : 'शुभ ग्रहों की दृष्टि व योग स्थिरता बनाए रखने में सहायक हैं।',
    astrologyTerminology: `Shubha Drishti & Benefic Yogas (${supportingInfluences.length} factors)`,
    promiseTerminology: supportScore >= 6 ? 'Strong' : 'Favorable',
    effectText: 'Shields against setbacks and enhances fortunate openings',
    effectTextHi: 'बाधाओं से रक्षा व अवसरों का विस्तार',
    scoreContribution: supportScore,
    isAfflicted: false,
    beneficInfluences: supportingInfluences,
    maleficInfluences: []
  };

  // 5. Rule 5: Contradicting Factors
  let contradictScore = -1;
  const saturn = kundali.planets.find(p => p.name === 'Saturn');
  const rahu = kundali.planets.find(p => p.name === 'Rahu');
  if (saturn && [targetHouseNum, (targetHouseNum + 2) % 12 + 1, (targetHouseNum + 6) % 12 + 1].includes(saturn.house)) {
    contradictScore -= 2;
    contradictingInfluences.push('Saturn disciplinary aspect or occupancy');
  }
  if (rahu && rahu.house === targetHouseNum) {
    contradictScore -= 3;
    contradictingInfluences.push('Rahu intensity & illusionary pressure in house');
  }

  const factor5: FiveRuleFactor = {
    ruleNumber: 5,
    factorName: 'Contradicting Factors',
    findingText: contradictingInfluences.length > 0
      ? `Points of caution identified: ${contradictingInfluences.join(', ')}.`
      : 'No major malefic obstructions; natural progress is safeguarded.',
    findingTextHi: contradictingInfluences.length > 0
      ? `सतर्कता बिंदु: ${contradictingInfluences.join(', ')}।`
      : 'कोई गंभीर बाधा उपस्थित नहीं है; मार्ग सुगम है।',
    astrologyTerminology: `Papa Drishti / Afflictions (${contradictingInfluences.length} points)`,
    promiseTerminology: contradictScore < -3 ? 'Challenged' : contradictScore < 0 ? 'Moderate' : 'Neutral',
    effectText: contradictScore < -2 ? 'Suggests conscious planning and protective remedies' : 'Mild friction easily mitigated through routine discipline',
    effectTextHi: contradictScore < -2 ? 'सजग योजना व उपचारात्मक उपायों की अनुशंसा' : 'सामान्य अनुशासन से सुगम समाधान',
    scoreContribution: contradictScore,
    isAfflicted: contradictingInfluences.length > 0,
    beneficInfluences: [],
    maleficInfluences: contradictingInfluences
  };

  const factors = [factor1, factor2, factor3, factor4, factor5];
  const rawSum = factors.reduce((sum, f) => sum + f.scoreContribution, 0);
  // Scale raw sum (-10 to +25) to 0 - 100
  const netPromiseScore = Math.max(15, Math.min(95, Math.round(50 + rawSum * 2.2)));

  let netAssessment: FiveRuleAssessmentNature = 'NEUTRAL';
  if (netPromiseScore >= 70) netAssessment = 'POSITIVE';
  else if (netPromiseScore <= 40) netAssessment = 'NEGATIVE';
  else if (contradictingInfluences.length > 0 && supportingInfluences.length > 0) netAssessment = 'MIXED';
  else netAssessment = 'POSITIVE';

  // Dual Terminology Formulation
  const techSummary = `${houseLordName} Lord of H${targetHouseNum}, Karaka ${primaryKarakaName}, Net Score ${netPromiseScore}/100`;
  const techSummaryHi = `भाव ${targetHouseNum} भावेश ${houseLordName}, कारक ${primaryKarakaName}, कुल अंक ${netPromiseScore}/100`;
  
  let promiseSummary = 'Moderate & Stable';
  let promiseSummaryHi = 'मध्यम एवं स्थिर';
  if (netAssessment === 'POSITIVE') {
    promiseSummary = 'Strong & Favorable';
    promiseSummaryHi = 'प्रबल एवं शुभ फलदायी';
  } else if (netAssessment === 'NEGATIVE') {
    promiseSummary = 'Challenged / Requires Care';
    promiseSummaryHi = 'सतर्कता एवं शांति उपाय अपेक्षित';
  } else if (netAssessment === 'MIXED') {
    promiseSummary = 'Mixed Potential with Growth';
    promiseSummaryHi = 'मिश्रित सामर्थ्य व अवसर';
  }

  return {
    houseNumber: targetHouseNum,
    themeId,
    houseSign,
    houseLord: houseLordName,
    primaryKaraka: primaryKarakaName,
    factors,
    netPromiseScore,
    netAssessment,
    dualTerminology: {
      technicalAstrology: techSummary,
      technicalAstrologyHi: techSummaryHi,
      clientPromise: promiseSummary,
      clientPromiseHi: promiseSummaryHi
    },
    deepReasoning: {
      supportSummary: supportingInfluences.join('; ') || 'General benefic balance across angular houses',
      supportSummaryHi: supportingInfluences.length > 0 ? 'शुभ ग्रहों का सकारात्मक संबल' : 'केंद्र व त्रिकोण में ग्रहों का संतुलन',
      challengeSummary: contradictingInfluences.join('; ') || 'No direct critical afflictions noted',
      challengeSummaryHi: contradictingInfluences.length > 0 ? 'सामयिक बाधा कारक' : 'कोई प्रमुख दोष विद्यमान नहीं',
      netVerdictExplanation: `Based on the five-rule assessment, House ${targetHouseNum} enjoys an aggregate promise score of ${netPromiseScore}%. ${netAssessment === 'POSITIVE' ? 'The auspicious factors heavily outweigh minor friction points.' : 'Focus on strengthening the significator planet.'}`,
      netVerdictExplanationHi: `पंच-सूत्रीय परीक्षण के अनुसार भाव ${targetHouseNum} को ${netPromiseScore}% का समग्र सामर्थ्य प्राप्त है।`
    }
  };
}
